'use client';

import { Skeleton } from '@mui/material';
import { useEffect, useRef } from 'react';

import { EmptyState } from '@web/ui';

import { ChatMessageItem } from './ChatMessageItem';
import styles from './ChatPanel.module.scss';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { ChatMessage } from '@/features/chat/types';

export function ChatMessageList({
  messages,
  isLoading,
  onReply,
  showSources = false,
  onSelectSource,
  currentUserId,
  currentUserEmail,
  onOpenDirectMessage,
  onOpenUserProfile,
}: {
  messages: ChatMessage[];
  isLoading: boolean;
  onReply: (message: ChatMessage) => void;
  showSources?: boolean;
  onSelectSource?: (chat: ActiveChat) => void;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onOpenDirectMessage?: (userId: string) => void;
  onOpenUserProfile?: (userId: string) => void;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const latestMessageId = messages.at(-1)?.id;

  useEffect(() => {
    if (isLoading || !latestMessageId) {
      return;
    }

    endRef.current?.scrollIntoView({ block: 'end' });
  }, [isLoading, latestMessageId]);

  if (isLoading) {
    return (
      <div className={styles.chatPanel__messages}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={76} />
        ))}
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className={styles.chatPanel__empty}>
        <EmptyState
          className={styles.chatPanel__emptyState}
          title="No messages yet."
          description="Messages for this channel will appear here."
        />
      </div>
    );
  }

  return (
    <div className={styles.chatPanel__messages}>
      {messages.map((message) => (
        <ChatMessageItem
          key={message.id}
          message={message}
          onReply={onReply}
          showSource={showSources}
          onSelectSource={onSelectSource}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          onOpenDirectMessage={onOpenDirectMessage}
          onOpenUserProfile={onOpenUserProfile}
        />
      ))}
      <div ref={endRef} />
    </div>
  );
}
